import { useEffect, useRef } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { API } from "@/lib/stream";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import BentoGridFeatures from "@/components/BentoGridFeatures";
import ModulePreviews from "@/components/ModulePreviews";
import HROpsSuite from "@/components/HROpsSuite";
import AiChatbotWidget from "@/components/AiChatbotWidget";
import AiDocDrafter from "@/components/AiDocDrafter";
import InterviewStudio from "@/components/InterviewStudio";
import RequisitionSection from "@/components/RequisitionSection";
import LeadCaptureCRM from "@/components/LeadCaptureCRM";
import FounderStory from "@/components/FounderStory";
import Opera360BridgeFooter from "@/components/Opera360BridgeFooter";
import AdminCRM from "@/pages/AdminCRM";

function Landing() {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <EditorialMarquee />
        <BentoGridFeatures />
        <ModulePreviews />
        <HROpsSuite />
        <AiChatbotWidget />
        <AiDocDrafter />
        <InterviewStudio />
        <RequisitionSection />
        <LeadCaptureCRM />
        <FounderStory />
      </main>
      <Opera360BridgeFooter />
    </>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    if (!sessionId) {
      navigate("/admin", { replace: true, state: { authError: "Missing session id" } });
      return;
    }
    fetch(`${API}/auth/google/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (r) => ({ ok: r.ok, d: await r.json() }))
      .then(({ ok, d }) => {
        if (ok) {
          localStorage.setItem("op360_access", d.access_token);
          localStorage.setItem("op360_refresh", d.refresh_token);
          navigate("/admin", { replace: true });
        } else {
          navigate("/admin", { replace: true, state: { authError: d.detail || "Google sign-in failed" } });
        }
      })
      .catch(() => navigate("/admin", { replace: true, state: { authError: "Google sign-in failed" } }));
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
      <div className="flex items-center gap-3 text-sm font-bold text-slate-500">
        <span className="w-4 h-4 rounded-full border-2 border-[#FF5722] border-t-transparent animate-spin" />
        Completing Google sign-in…
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin" element={<AdminCRM />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-[#FBFBF9] text-slate-900">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
